/*
 * Staff back office — /staff
 *
 * Waitstaff/cashier dashboard. Locked to logged-in users with is_staff or
 * is_admin. Non-authed visitors get the PIN keypad gate. Regular customers
 * see a "Not a staff account" nudge.
 *
 * Features
 *   • Today's active orders (paid → preparing → ready → completed)
 *   • Advance status inline with SMS + print side effects (via /api/staff/...)
 *   • Today's reservations + shared shift notes
 *   • Bell chime on new paid orders — polls every 8 s (orders + reservations)
 *   • Volume slider + browser push notification opt-in — device-local
 *   • Auto-refresh order list every 12 s so it stays current on a wall-mounted
 *     till tablet.
 *
 * Reuses AuthModal for the password login prompt (mounted in App root).
 * Extracted sub-components live in /pages/staff/ to keep this file focused
 * on the polling + role-gating logic.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Bell, CalendarCheck, CheckCircle2, ChefHat, Loader2, Utensils
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useOfficeManifest } from "../hooks/useOfficeManifest";

// Extracted sub-components (see /pages/staff/*)
import PinLoginGate from "./staff/PinLoginGate";
import PinManageModal from "./staff/PinManageModal";
import TopBar from "./staff/TopBar";
import ShiftNotesPanel from "./staff/ShiftNotesPanel";
import BirthdayTreatsPanel from "./staff/BirthdayTreatsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 8000;
const REFRESH_MS = 12000;

// LocalStorage keys — device-local (each till tablet keeps its own preferences).
const LS_VOLUME = "mm_staff_bell_volume";   // 0..1
const LS_PUSH   = "mm_staff_push_enabled";  // "1" | "0"

// Simple bell chime generated on the fly via Web Audio — no asset file needed.
// `volume` is the max gain (0..1). 0 = silent.
function playBell(volume = 0.28) {
  if (volume <= 0) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const tones = [880, 1320]; // A5 + E6, warm two-note chime
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.14);
      gain.gain.linearRampToValueAtTime(volume, now + i * 0.14 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.14 + 0.7);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.14);
      osc.stop(now + i * 0.14 + 0.75);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch (err) {
    console.warn("[staff] bell chime failed", err?.name || err);
  }
}

function firePush(count) {
  // Only when the tab is backgrounded — otherwise the on-page toast+bell is enough.
  if (typeof document === "undefined" || document.visibilityState !== "hidden") return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const n = new Notification(
      count === 1 ? "New order in" : `${count} new orders in`,
      { body: "Open Mamamina's back office to serve them.", tag: "mm-staff-orders" }
    );
    // Focus the tab when clicked
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) { console.warn("[staff] order push failed", err?.name || err); }
}

function fireReservationPush(count) {
  if (typeof document === "undefined" || document.visibilityState !== "hidden") return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  try {
    const n = new Notification(
      count === 1 ? "New reservation" : `${count} new reservations`,
      { body: "Someone just booked a table.", tag: "mm-staff-reservations" }
    );
    n.onclick = () => { window.focus(); n.close(); };
  } catch (err) { console.warn("[staff] reservation push failed", err?.name || err); }
}

export default function Staff() {
  const { user, isAuthenticated, loading, authHeaders, isStaff, isAdmin, canStaff, setModal, logout, pinLogin } = useAuth();
  const { toast } = useToast();
  // Staff back office installs as part of Mamamina Office too
  useOfficeManifest();

  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [busy, setBusy] = useState(true);
  const [pinModal, setPinModal] = useState(false);
  const [lastPoll, setLastPoll] = useState(new Date().toISOString());
  const [lastResPoll, setLastResPoll] = useState(new Date().toISOString());
  const [volume, setVolume] = useState(() => {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(LS_VOLUME) : null;
    const n = raw == null ? 0.6 : parseFloat(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.6;
  });
  const [pushEnabled, setPushEnabled] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    if (typeof Notification === "undefined") return false;
    return localStorage.getItem(LS_PUSH) === "1" && Notification.permission === "granted";
  });
  const [newAlert, setNewAlert] = useState(0);
  const volumeRef = useRef(volume);
  useEffect(() => { volumeRef.current = volume; localStorage.setItem(LS_VOLUME, String(volume)); }, [volume]);
  const pushRef = useRef(pushEnabled);
  useEffect(() => { pushRef.current = pushEnabled; }, [pushEnabled]);

  const soundOn = volume > 0;
  const toggleSound = () => setVolume((v) => (v > 0 ? 0 : 0.6));

  const requestPush = async () => {
    if (typeof Notification === "undefined") {
      toast({ title: "Not supported", description: "Your browser doesn't support push notifications." });
      return;
    }
    if (Notification.permission === "granted") {
      setPushEnabled(true);
      localStorage.setItem(LS_PUSH, "1");
      toast({ title: "Notifications on", description: "You'll be alerted when this tab is in the background." });
      return;
    }
    if (Notification.permission === "denied") {
      toast({
        title: "Blocked by browser",
        description: "Enable notifications for this site in your browser settings, then try again.",
      });
      return;
    }
    const result = await Notification.requestPermission();
    if (result === "granted") {
      setPushEnabled(true);
      localStorage.setItem(LS_PUSH, "1");
      toast({ title: "Notifications on", description: "You'll be alerted when this tab is in the background." });
    } else {
      toast({ title: "Notifications off", description: "You can turn these on later from the header." });
    }
  };
  const disablePush = () => {
    setPushEnabled(false);
    localStorage.setItem(LS_PUSH, "0");
    toast({ title: "Notifications off" });
  };

  const load = useCallback(async () => {
    if (!canStaff) return;
    try {
      const [o, r] = await Promise.all([
        axios.get(`${API}/staff/orders`, { headers: authHeaders }),
        axios.get(`${API}/staff/reservations`, { headers: authHeaders }),
      ]);
      setOrders(o.data.items || []);
      setReservations(r.data.items || []);
    } catch (e) {
      toast({ title: "Refresh failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }, [canStaff, authHeaders, toast]);

  // Initial load + full refresh interval
  useEffect(() => {
    if (!canStaff) return;
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [canStaff, load]);

  // Bell — poll new-count using the moving `lastPoll` timestamp
  useEffect(() => {
    if (!canStaff) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/staff/orders/new-count`, {
          headers: authHeaders,
          params: { since: lastPoll },
        });
        if (data.count > 0) {
          setNewAlert((v) => v + data.count);
          playBell(volumeRef.current);
          if (pushRef.current) firePush(data.count);
          toast({
            title: data.count === 1 ? "New order in" : `${data.count} new orders in`,
            description: "Ring — check the queue.",
          });
          setLastPoll(data.server_time || new Date().toISOString());
          load();
        }
      } catch (err) { console.warn("[staff] order poll blip", err?.response?.status || err?.message); }
    };
    const t = setInterval(tick, POLL_MS);
    return () => clearInterval(t);
  }, [canStaff, authHeaders, lastPoll, load, toast]);

  // Second bell — reservations. Same POLL_MS, same chime + push channel so
  // the till doesn't get two independent noise streams.
  useEffect(() => {
    if (!canStaff) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/staff/reservations/new-count`, {
          headers: authHeaders,
          params: { since: lastResPoll },
        });
        if (data.count > 0) {
          playBell(volumeRef.current);
          if (pushRef.current) fireReservationPush(data.count);
          toast({
            title: data.count === 1 ? "New reservation" : `${data.count} new reservations`,
            description: "Someone just booked a table.",
          });
          setLastResPoll(data.server_time || new Date().toISOString());
          load();
        }
      } catch (err) { console.warn("[staff] reservation poll blip", err?.response?.status || err?.message); }
    };
    const t = setInterval(tick, POLL_MS);
    return () => clearInterval(t);
  }, [canStaff, authHeaders, lastResPoll, load, toast]);

  const setStatus = async (session_id, status) => {
    try {
      const { data } = await axios.patch(`${API}/staff/orders/${session_id}/status`, { status }, { headers: authHeaders });
      toast({
        title: `Marked ${status}`,
        description: data.sms_sent ? "Customer notified by SMS." : "Status saved.",
      });
      load();
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail || e.message });
    }
  };

  // --- Gate 1: still loading auth ---
  if (loading) {
    return (
      <div className="mm-dark min-h-screen bg-[var(--mm-bg)] flex items-center justify-center">
        <Loader2 size={32} className="text-[var(--mm-accent)] animate-spin" />
      </div>
    );
  }

  // --- Gate 2: not logged in ---
  if (!isAuthenticated) {
    return <PinLoginGate onPasswordLogin={() => setModal("login")} onPinSuccess={() => { /* auth state re-renders */ }} pinLogin={pinLogin} toast={toast} />;
  }

  // --- Gate 3: not staff ---
  if (!canStaff) {
    return (
      <GateShell
        title="Not a staff account"
        body={`You're signed in as ${user.email}, but you don't have back-office access yet. Ask Mamamina to promote your account from the Admin › Users tab.`}
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/account" className="btn-outline">Back to my account</Link>
          <button onClick={logout} className="btn-primary">Sign out</button>
        </div>
      </GateShell>
    );
  }

  const activeOrders = orders.filter((o) => ["paid", "preparing", "ready"].includes(o.fulfillment_status || "paid"));
  const doneToday = orders.filter((o) => (o.fulfillment_status || "") === "completed");

  return (
    <div className="mm-dark min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
        <TopBar
          user={user}
          isAdmin={isAdmin}
          volume={volume}
          soundOn={soundOn}
          onToggleSound={toggleSound}
          onSetVolume={setVolume}
          pushEnabled={pushEnabled}
          onEnablePush={requestPush}
          onDisablePush={disablePush}
          onManagePin={() => setPinModal(true)}
          onRefresh={load}
          onLogout={logout}
          newAlert={newAlert}
          onClearAlert={() => setNewAlert(0)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Active Orders" value={activeOrders.length} icon={<Utensils size={16} />} testid="stat-active" />
          <StatCard label="Ready to Serve" value={activeOrders.filter((o) => o.fulfillment_status === "ready").length} icon={<CheckCircle2 size={16} />} testid="stat-ready" />
          <StatCard label="Completed Today" value={doneToday.length} icon={<ChefHat size={16} />} testid="stat-completed" />
          <StatCard label="Reservations Today" value={reservations.length} icon={<CalendarCheck size={16} />} testid="stat-reservations" />
        </div>

        {busy ? (
          <div className="py-24 text-center">
            <Loader2 size={28} className="mx-auto text-[var(--mm-accent)] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <OrderColumn title="In flight" testid="order-column" orders={activeOrders} onSetStatus={setStatus} />
              {doneToday.length > 0 && (
                <CompletedList orders={doneToday.slice(0, 8)} />
              )}
            </div>
            <ReservationsAndNotes reservations={reservations} authHeaders={authHeaders} userId={user.id} isAdmin={isAdmin} toast={toast} />
          </div>
        )}
      </div>

      {pinModal && (
        <PinManageModal
          user={user}
          authHeaders={authHeaders}
          toast={toast}
          onClose={(refresh) => {
            setPinModal(false);
            if (refresh) {
              // Force AuthContext to re-fetch /auth/me so pin_set flips
              axios.get(`${API}/auth/me`, { headers: authHeaders }).then(({ data }) => {
                try {
                  const raw = JSON.parse(localStorage.getItem("mm_auth") || "{}");
                  localStorage.setItem("mm_auth", JSON.stringify({ ...raw, user: data.user }));
                  window.location.reload();
                } catch (err) { console.warn("[staff] pin refresh reload failed", err); }
              });
            }
          }}
        />
      )}
    </div>
  );
}

function ReservationsAndNotes({ reservations, authHeaders, userId, isAdmin, toast }) {
  return (
    <div className="space-y-6">
      <ReservationList reservations={reservations} />
      <ShiftNotesPanel authHeaders={authHeaders} userId={userId} isAdmin={isAdmin} toast={toast} />
      <BirthdayTreatsPanel authHeaders={authHeaders} toast={toast} />
    </div>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div className="p-4 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid={testid}>
      <div className="text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] flex items-center gap-2 mb-2">
        {icon} {label}
      </div>
      <div className="font-display italic text-4xl text-[var(--mm-cream)] leading-none">{value}</div>
    </div>
  );
}

function OrderColumn({ title, orders, onSetStatus, testid }) {
  if (orders.length === 0) {
    return (
      <div className="p-12 text-center border border-dashed border-[var(--mm-line)]" data-testid={testid}>
        <Bell size={22} className="mx-auto text-[var(--mm-cream-muted)] mb-3" />
        <div className="font-display italic text-xl text-[var(--mm-cream)]">No orders in the queue.</div>
        <div className="text-xs text-[var(--mm-cream-muted)] tracking-[0.2em] uppercase mt-2">
          The bell will ring when a fresh one lands.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="section-label">{title}</div>
      {orders.map((o) => (
        <OrderCard key={o.session_id} order={o} onSetStatus={onSetStatus} />
      ))}
    </div>
  );
}

function OrderCard({ order, onSetStatus }) {
  const status = order.fulfillment_status || "paid";
  const items = order.items || [];
  const total = (order.amount || 0) / 100;
  const time = order.created_at ? new Date(order.created_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "";
  const name = order.customer_name || order.customer_email || "Guest";
  const orderType = (order.metadata?.order_type || order.order_type || "takeaway").toLowerCase();
  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]" data-testid={`order-card-${order.session_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className={`inline-block px-2 py-0.5 text-[0.58rem] tracking-[0.22em] uppercase border ${statusColor(status)}`}>
              {status}
            </span>
            <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">{orderType} · {time}</span>
          </div>
          <div className="font-display italic text-xl text-[var(--mm-cream)]">{name}</div>
          {order.customer_phone && (
            <a href={`tel:${order.customer_phone}`} className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-accent)]">
              {order.customer_phone}
            </a>
          )}
        </div>
        <div className="text-right">
          <div className="font-display italic text-2xl text-[var(--mm-cream)]">${total.toFixed(2)}</div>
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">{items.length} item{items.length !== 1 ? "s" : ""}</div>
        </div>
      </div>
      {items.length > 0 && (
        <ul className="mt-3 space-y-1">
          {items.map((it, i) => (
            <li key={`${it.lookup_key || "item"}-${i}`} className="text-sm text-[var(--mm-cream-muted)] flex items-center gap-2">
              <span className="text-[var(--mm-accent)]">×{it.quantity}</span>
              <span>{friendlyName(it)}</span>
            </li>
          ))}
        </ul>
      )}
      {order.customer_notes && (
        <div className="mt-3 p-3 border border-dashed border-[var(--mm-line)] text-sm text-[var(--mm-cream)] italic">
          &ldquo;{order.customer_notes}&rdquo;
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {status !== "preparing" && status !== "ready" && status !== "completed" && (
          <button data-testid={`btn-preparing-${order.session_id}`} onClick={() => onSetStatus(order.session_id, "preparing")} className="btn-outline text-xs">
            Start preparing
          </button>
        )}
        {status !== "ready" && status !== "completed" && (
          <button data-testid={`btn-ready-${order.session_id}`} onClick={() => onSetStatus(order.session_id, "ready")} className="btn-primary text-xs">
            Mark ready
          </button>
        )}
        {status === "ready" && (
          <button data-testid={`btn-completed-${order.session_id}`} onClick={() => onSetStatus(order.session_id, "completed")} className="btn-outline text-xs">
            Handed over
          </button>
        )}
      </div>
    </div>
  );
}

function CompletedList({ orders }) {
  return (
    <div>
      <div className="section-label mb-3">Completed today</div>
      <div className="space-y-1.5">
        {orders.map((o) => (
          <div key={o.session_id} className="p-3 border border-[var(--mm-line)]/60 bg-[var(--mm-bg-alt)]/40 flex items-center justify-between text-sm">
            <div className="text-[var(--mm-cream-muted)]">
              <span className="text-[var(--mm-cream)]">{o.customer_name || "Guest"}</span> — {(o.items || []).length} item{(o.items || []).length !== 1 ? "s" : ""}
            </div>
            <div className="text-[var(--mm-cream-muted)] font-mono text-xs">${((o.amount || 0) / 100).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReservationList({ reservations }) {
  return (
    <div>
      <div className="section-label mb-3 flex items-center gap-2"><CalendarCheck size={13} /> Today&apos;s reservations</div>
      {reservations.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-[var(--mm-line)] text-[var(--mm-cream-muted)] text-sm">
          Nothing on the books for today.
        </div>
      ) : (
        <div className="space-y-2">
          {reservations.map((r) => (
            <div key={r.id} data-testid={`reservation-${r.id}`} className="p-3 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
              <div className="flex items-center justify-between">
                <div className="font-display italic text-lg text-[var(--mm-cream)]">{r.name || "Guest"}</div>
                <div className="text-[var(--mm-accent)] font-mono text-sm">{r.time || "—"}</div>
              </div>
              <div className="text-xs text-[var(--mm-cream-muted)] mt-0.5">
                Party of {r.party_size || "?"}{r.phone ? ` · ${r.phone}` : ""}
              </div>
              {r.notes && (
                <div className="text-xs text-[var(--mm-cream)]/80 italic mt-1">&ldquo;{r.notes}&rdquo;</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GateShell({ title, body, children }) {
  return (
    <div className="mm-dark min-h-screen bg-[var(--mm-bg)] grain flex items-center justify-center px-6">
      <div className="max-w-lg text-center">
        <div className="section-label mb-4 flex items-center justify-center gap-2">
          <ChefHat size={13} /> Back office
        </div>
        <h1 className="font-display italic text-4xl md:text-5xl text-[var(--mm-cream)] leading-tight mb-4">{title}</h1>
        <p className="text-[var(--mm-cream-muted)] font-light mb-8">{body}</p>
        <div className="flex flex-wrap gap-3 justify-center">{children}</div>
      </div>
    </div>
  );
}

function statusColor(s) {
  return {
    paid: "border-[var(--mm-cream)] text-[var(--mm-cream)]",
    preparing: "border-[var(--mm-accent)] text-[var(--mm-accent)] bg-[var(--mm-accent)]/10",
    ready: "border-green-400 text-green-400 bg-green-400/10",
    completed: "border-[var(--mm-cream-muted)] text-[var(--mm-cream-muted)]",
    cancelled: "border-red-400 text-red-400",
  }[s] || "border-[var(--mm-line)] text-[var(--mm-cream)]";
}

function friendlyName(it) {
  if (it.name) return it.name;
  if (!it.lookup_key) return "Menu item";
  return it.lookup_key
    .replace(/^mamamina_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
